import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import ContentRail from "@/components/ContentRail";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Play, Lock } from "lucide-react";
import PaidUnlockButton from "@/components/PaidUnlockButton";

export default function Browse() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);

  const params = new URLSearchParams(location.search);
  const filterType = params.get("type");
  const justPaid = params.get("just_paid") === "1";

  useEffect(() => {
    if (!loading && !user) {
      navigate("/", { replace: true });
    }
  }, [loading, user, navigate]);

  // Fix for "needs multiple refreshes after PayPal/Stripe" bug.
  // When user lands on /browse?just_paid=1 but is_subscribed is still false,
  // re-fetch /auth/me up to 8 times (every 1.5s) — the DB write usually
  // propagates within a couple seconds. We strip ?just_paid once unlocked.
  useEffect(() => {
    if (!justPaid || !user || user.is_subscribed) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled || attempts >= 8) return;
      attempts += 1;
      await new Promise((r) => setTimeout(r, 1500));
      if (cancelled) return;
      await refresh();
    };
    tick();
    const id = setInterval(tick, 1700);
    return () => { cancelled = true; clearInterval(id); };
  }, [justPaid, user, refresh]);

  // Clean ?just_paid out of URL once the user is verified subscribed.
  useEffect(() => {
    if (justPaid && user?.is_subscribed) {
      const url = new URL(window.location.href);
      url.searchParams.delete("just_paid");
      window.history.replaceState({}, "", url.toString());
    }
  }, [justPaid, user]);

  useEffect(() => {
    api.get("/content").then((r) => setItems(r.data)).catch(() => {});
  }, [user?.is_subscribed]);

  const featured = useMemo(() => items.find((i) => i.featured) || items[0], [items]);

  const groups = useMemo(() => {
    const all = filterType ? items.filter((i) => i.type === filterType) : items;
    return {
      coming: all.filter((i) => i.coming_soon),
      movie: all.filter((i) => i.type === "movie" && !i.coming_soon),
      show: all.filter((i) => i.type === "show" && !i.coming_soon),
      trailer: all.filter((i) => i.type === "trailer" && !i.coming_soon),
      interview: all.filter((i) => i.type === "interview" && !i.coming_soon),
    };
  }, [items, filterType]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A0A0E] grid place-items-center text-gray-400">Loading…</div>
    );
  }

  const goSubscribe = () => navigate("/subscribe");

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white">
      <Header />

      {featured && (
        <section className="relative min-h-[85vh] flex items-end pb-16 overflow-hidden">
          <img
            src={featured.backdrop_url || featured.poster_url}
            alt={featured.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-fade" />
          <div className="absolute inset-0 hero-fade-side" />

          <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 w-full">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-4">
                {featured.type === "movie" ? "Featured Film" : featured.type === "show" ? "Featured Show" : "Featured"}
              </div>
              <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
                {featured.title}
              </h1>
              <div className="mt-3 flex items-center gap-3 text-sm text-gray-300">
                {featured.year && <span>{featured.year}</span>}
                {featured.genre && <span className="px-2 py-0.5 border border-white/15 rounded-full text-xs uppercase tracking-wider">{featured.genre}</span>}
                {featured.duration && <span>{featured.duration}</span>}
              </div>
              <p className="mt-5 text-gray-300 max-w-xl leading-relaxed">{featured.description}</p>

              <div className="mt-7 flex flex-wrap gap-3">
                <Button
                  data-testid="featured-play"
                  onClick={() => {
                    if (!user.is_subscribed && featured.type !== "trailer") goSubscribe();
                    else navigate(`/watch/${featured.id}`);
                  }}
                  className="rounded-full bg-white text-black hover:bg-gray-200 px-7 py-6 text-base font-semibold"
                >
                  {!user.is_subscribed && featured.type !== "trailer" ? (
                    <><Lock className="w-4 h-4 mr-2"/>Subscribe to play</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2 fill-black"/>Play</>
                  )}
                </Button>
                <Button
                  data-testid="featured-more"
                  onClick={() => navigate(`/watch/${featured.id}`)}
                  variant="outline"
                  className="rounded-full bg-white/10 hover:bg-white/15 border-white/20 text-white px-7 py-6 text-base font-semibold"
                >
                  More info
                </Button>
              </div>

              {!user.is_subscribed && (
                <div data-testid="sub-banner" className="mt-6 flex items-center gap-4 flex-wrap">
                  <div className="inline-flex items-center gap-3 rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] text-sm px-4 py-2">
                    <Lock className="w-4 h-4" /> You're previewing. <button onClick={goSubscribe} className="underline ml-1">Unlock everything for $3.99/mo</button>
                  </div>
                  <PaidUnlockButton />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-12 space-y-12">
        {groups.coming.length > 0 && (
          <ContentRail title="Coming Soon" items={groups.coming} locked={false} />
        )}
        {(!filterType || filterType === "movie") && (
          <ContentRail title="Movies" items={groups.movie} locked={!user.is_subscribed} />
        )}
        {(!filterType || filterType === "show") && (
          <ContentRail title="Shows" items={groups.show} locked={!user.is_subscribed} />
        )}
        {(!filterType || filterType === "trailer") && (
          <ContentRail title="Trailers" items={groups.trailer} locked={false} />
        )}
        {(!filterType || filterType === "interview") && (
          <ContentRail title="Interviews" items={groups.interview} locked={!user.is_subscribed} />
        )}
      </section>
    </div>
  );
}
